import _get from "lodash-es/get";
import _isEmpty from "lodash-es/isEmpty";
import { call, put, select } from "redux-saga/effects";
import { ActionType } from "typesafe-actions";

import * as formActions from "src/ipm-shared/components/Form/actions";
import RestClient from "src/ipm-shared/services/Rest";
import { RootState } from "src/ipm-shared/store/model/reducers";
import { catchTakeLatest } from "src/ipm-shared/Utils/ReduxSagaEffects";
import T from "src/ipm-shared/Utils/Intl";

import * as selectors from "src/ipm-platform/pages/Register/selectors";

import { ENTERPRISE_ONBOARDING_FORM } from "./const";
import * as enterpriseOnboardingActions from "./actions";

const actions = {
  ...formActions,
  ...enterpriseOnboardingActions
};

const watchedSagas = [
  catchTakeLatest(
    actions.enterpriseOnboardingSubmit,
    handleEnterpriseOnboardingSubmit
  )
];

export default watchedSagas;

export function* handleEnterpriseOnboardingSubmit(
  action: ActionType<typeof actions.enterpriseOnboardingSubmit>
) {
  const { cb } = action.payload;
  const state: RootState = yield select();
  const formState = selectors.getControls(state, ENTERPRISE_ONBOARDING_FORM);
  const bAcraBizfile = _get(formState, "b_acra_bizfile.value", undefined);
  const bStructuredChart = _get(
    formState,
    "b_structured_chart.value",
    undefined
  );
  const bLetterOfAuthorization = _get(
    formState,
    "b_letter_of_authorization.value",
    undefined
  );
  const bNp2aNric = _get(formState, "b_np2a_nric.value", undefined);
  const bShareholder = _get(formState, "b_shareholder.value", undefined);
  const bShareholderPoa = _get(formState, "b_shareholder_poa.value", undefined);
  const bShareholderCorporate = _get(
    formState,
    "b_shareholder_corporate.value",
    undefined
  );

  const companyLegalName = _get(formState, "company_legal_name.value", null);
  const countryOfIncorporation = _get(
    formState,
    "country_of_incorporation.value",
    null
  );
  const companyWebsite = _get(formState, "company_website.value", null);
  const parentPublicEntity = _get(
    formState,
    "parent_public_entity.value",
    null
  );
  const parentCompanyLegalName = _get(
    formState,
    "parent_company_legal_name.value",
    null
  );
  const attestationRepresentativeName = _get(
    formState,
    "attestation_representative_name.value",
    null
  );
  const attestationRepresentativeDesignation = _get(
    formState,
    "attestation_representative_designation.value",
    null
  );

  const setRequiredField = (name: string) =>
    formActions.setControl({
      displayError: true,
      errors: [
        {
          code: "REQUIRED_FIELD",
          message: T.transl("REQUIRED_FIELD")
        }
      ],
      form: ENTERPRISE_ONBOARDING_FORM,
      name
    });

  const requiredFields: Array<{ name: string; value: any }> = [
    { name: "company_legal_name", value: companyLegalName },
    { name: "country_of_incorporation", value: countryOfIncorporation },
    { name: "company_website", value: companyWebsite },
    { name: "b_acra_bizfile", value: bAcraBizfile },
    { name: "parent_public_entity", value: parentPublicEntity },
    { name: "parent_company_legal_name", value: parentCompanyLegalName },
    { name: "b_structured_chart", value: bStructuredChart },
    { name: "b_letter_of_authorization", value: bLetterOfAuthorization },
    { name: "b_np2a_nric", value: bNp2aNric },
    {
      name: "attestation_representative_name",
      value: attestationRepresentativeName
    },
    {
      name: "attestation_representative_designation",
      value: attestationRepresentativeDesignation
    }
  ];

  let hasMissingRequired = false;
  for (const field of requiredFields) {
    if (!field.value) {
      yield put(setRequiredField(field.name));
      hasMissingRequired = true;
    }
  }

  if (hasMissingRequired) {
    return;
  }

  const dataRequest = {
    body: {
      company_legal_name: companyLegalName,
      country_of_incorporation: countryOfIncorporation,
      company_website: companyWebsite,
      parent_public_entity: parentPublicEntity,
      parent_company_legal_name: parentCompanyLegalName,
      attestation_representative_name: attestationRepresentativeName,
      attestation_representative_designation: attestationRepresentativeDesignation,
      b_acra_bizfile: bAcraBizfile ? bAcraBizfile.split(",") : null,
      b_structured_chart: bStructuredChart ? bStructuredChart.split(",") : null,
      b_shareholder: bShareholder ? bShareholder.split(",") : null,
      b_shareholder_poa: bShareholderPoa ? bShareholderPoa.split(",") : null,
      b_shareholder_corporate: bShareholderCorporate
        ? bShareholderCorporate.split(",")
        : null,
      b_letter_of_authorization: bLetterOfAuthorization
        ? bLetterOfAuthorization.split(",")
        : null,
      b_np2a_nric: bNp2aNric ? bNp2aNric.split(",") : null
    },
    service: "create_enterprise_onboarding",
    showGlobalLoader: true
  };

  const res: Response = yield call(RestClient.send, dataRequest);

  if (!res) {
    return;
  }

  const errors = _get(res, "errors", {});
  if (!_isEmpty(errors)) {
    yield put(actions.parseServerErrors(errors, ENTERPRISE_ONBOARDING_FORM));
    return;
  }

  if (cb) {
    cb();
  }
}
